import Papa, {ParseResult as PapaParseResult} from 'papaparse';
import {groupAndSortSamplesByReplicates} from '../../../../utils/chip';

/**
 * Parses a CSV file with headers and returns an array of objects.
 *
 * Promisified wrapper of Papa.parse, with some configuration defaults and our
 * way of handling errors.
 *
 * Rejects with a list of error messages if the CSV file is invalid.
 */
const papaParse = async (file: File): Promise<Record<string, string>[]> => {
    // PapaParse auto-detects one newline sequence for the whole file. If a CSV
    // mostly uses CRLF but ends with LF (or otherwise mixes line endings), the
    // unmatched newline can become part of the final cell value. Normalize the
    // input first so every record separator is interpreted consistently.
    const contents = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error ?? new Error('Unable to read CSV file'));
        reader.readAsText(file);
    });
    const normalizedContents = contents.replace(/\r\n|\r|\n/g, '\n');

    return new Promise((resolve, reject) => {
        Papa.parse(normalizedContents, {
            header: true,
            skipEmptyLines: true,
            // Ensure values are not parsed as numbers/booleans.
            dynamicTyping: false,
            complete: (result: PapaParseResult<Record<string, string>>) => {
                // PapaParse can still return a parsed result when there are errors.
                // After trying it out, most of the errors should just lead to
                // stopping execution and telling the user parsing failed.
                if (result.errors.length > 0) {
                    // This error also happens with an empty file, which is a
                    // confusing error to give to the user. Filter it out and
                    // let later validation handle it.
                    const errorForUser = result.errors.filter(error => error.code !== 'UndetectableDelimiter');
                    if (errorForUser.length > 0) {
                        reject(errorForUser.map(error => `${error.message} on row ${error.row}`));
                        return;
                    }
                }
                resolve(result.data);
            },
            error: (error: Error) => {
                // Handle fundamental PapaParse errors (e.g., file reading issues)
                console.error('PapaParse critical error:', error);
                reject([`An unknown error occurred during CSV parsing: ${error.message}`]);
            }
        });
    });
};

const isIlluminaFormat = (filename: string): boolean => {
    const filenameParts = filename.split('_');
    if (filenameParts.length < 5) return false;
    const regex = new RegExp(/^[a-zA-Z0-9-_.]+_S[1-9][0-9]{0,7}_L00[1-4]_R[1-2]_001\.fastq\.gz$/);
    const regexResult = regex.test(filename);
    return regexResult;
};

/**
 * Checks whether an Illumina-formatted filename's read number (R1 or R2)
 * is compatible with the experiment's sequencing read type.
 *
 * For single-end experiments, only R1 filenames are valid.
 * For paired-end experiments, both R1 and R2 filenames are valid.
 */
const isIlluminaReadTypeMatch = (filename: string, sequencing_read_type: string): boolean => {
    if (sequencing_read_type === 'singled') {
        // Single-end: only R1 files are expected
        return /_R1_001\.fastq\.gz$/.test(filename);
    }
    // Paired: both R1 and R2 are valid per file
    return true;
};

const isNCBIGeoFormat = (filename: string, sequencing_read_type: string): boolean => {
    const filenameParts = filename.split('_');
    if (sequencing_read_type === 'paired') {
        if (filenameParts.length !== 2) return false;
        const regex = new RegExp(/^SRR[1-9][0-9]{3,7}_[1-2]\.fastq\.gz$/);
        const regexResult = regex.test(filename);
        return regexResult;
    }
    if (sequencing_read_type === 'singled') {
        if (filenameParts.length !== 1) return false;
        const regex = new RegExp(/^SRR[1-9][0-9]{3,7}\.fastq\.gz$/);
        const regexResult = regex.test(filename);
        return regexResult;
    }
    return false;
};

/**
 * Checks whether a filename is valid. A filename is valid if it is in
 * Illumina format or NCBI GEO format.
 */
const isValidFilenameFormat = (filename: string, sequencing_read_type: string): boolean => {
    return isIlluminaFormat(filename) || isNCBIGeoFormat(filename, sequencing_read_type);
};

/**
 * Given a list of rows from a CSV file. The list might repeat sample
 * information in multiple rows, once for every file associated with a sample.
 * A row will have values for the given column names. This function creates a
 * list of rows where the filenames are removed and the samples are unique.
 * Columns are only kept if they have multiple different values across the
 * rows.
 *
 * Currently used with RNA CSV files.
 *
 * @return list of objects with properties SampleName and the column names that had multiple values
 */
export const parseValidSamples = (csvrows: Record<string, string>[]): Record<string, string>[] => {
    // csvrows[0] is the first data row. A row is parsed as an object with
    // keys as column names.
    const columns = Object.keys(csvrows[0]);
    // A "category" is a column name. Specifically, besides the standard
    // ones (samplename, filename), the categories will be the variables
    // that change between different measurements. See the group options
    // etc.
    const categoriesWithMultipleValues: string[] = [];
    columns.forEach((column: string) => {
        if (column === 'SampleName' || column === 'Filename') return;
        const uniqueValuesInColumn: Set<string> = new Set(csvrows.map((row: any) => row[column]));
        if (uniqueValuesInColumn.size > 1) {
            categoriesWithMultipleValues.push(column);
        }
    });

    // Sample rows are duplicated for each file. Grab the first row for
    // each unique sample name. Of a row, only keep the categories with
    // multiple values as well as the sample's name.
    const rawFirstOfEachUniqueSample: any[] = [];
    for (const row of csvrows) {
        if (rawFirstOfEachUniqueSample.some((obj: any) => obj.SampleName === row.SampleName)) continue;
        rawFirstOfEachUniqueSample.push(row);
    }

    const firstOfEachUniqueSample = rawFirstOfEachUniqueSample.map((row: any) => {
        const filteredRow: Record<string, string> = {};
        categoriesWithMultipleValues.forEach((column: string) => {
            filteredRow[column] = row[column];
        });
        filteredRow.SampleName = row.SampleName;
        return filteredRow;
    });

    return firstOfEachUniqueSample;
};

/**
 * Given a list of rows from a CSV file, this function returns a dictionary
 * where the keys are sample names and the values are lists of filenames. In
 * the input RNA CSV, we currently have the user have a row for a sample
 * duplicated entirely except for the filename. So if a sample has two files,
 * the CSV will have
 *
 * ```csv
 * sample1,file1.fastq.gz
 * sample1,file2.fastq.gz
 * ```
 *
 * This function gets that information out in a structured manner and would then return
 *
 * ```json
 * {"sample1": ["file1.fastq.gz", "file2.fastq.gz"]}
 * ```
 *
 * Currently used with RNA CSV files.
 */
export const mapSamplenameToFilenames = (csvrows: Record<string, string>[]): Record<string, string[]> => {
    const sampleNameToFilenames: Record<string, string[]> = {};
    for (const row of csvrows) {
        if (!sampleNameToFilenames[row.SampleName]) {
            sampleNameToFilenames[row.SampleName] = [];
        }
        sampleNameToFilenames[row.SampleName].push(row.Filename);
    }
    return sampleNameToFilenames;
};

/**
 * Given a mapping of sample names to filenames, a list of fastq files, and the
 * sequencing read type, this function filters out invalid filenames (i.e.,
 * those not following Illumina or NCBI GEO formatting) and unused filenames
 * (i.e., those not present in the provided fastq files).
 *
 * @returns a new mapping of sample names to valid and used File objects.
 */
export const filterInvalidOrUnusedFilenames = (
    samplenameToFilenames: Record<string, string[]>,
    fastq_files: File[],
    sequencing_read_type: string
): Record<string, File[]> => {
    const usedFilenames: Map<string, File> = fastq_files.reduce((acc: Map<string, File>, file: File) => {
        acc.set(file.name, file);
        return acc;
    }, new Map<string, File>());
    const filteredFiles: Record<string, File[]> = {};
    for (const [samplename, filenames] of Object.entries(samplenameToFilenames)) {
        const validFiles: File[] = [];
        for (const filename of filenames) {
            if (!isValidFilenameFormat(filename, sequencing_read_type)) {
                console.warn(`Invalid filename format: ${filename}`);
                continue;
            }
            if (!usedFilenames.has(filename)) {
                console.warn(`Filename not found in fastq files: ${filename}`);
                continue;
            }
            validFiles.push(usedFilenames.get(filename)!);
        }
        if (validFiles.length > 0) {
            filteredFiles[samplename] = validFiles;
        }
    }
    return filteredFiles;
};

type ValidationSuccess = {
    status: 'success';
};
type ValidationFailure = {
    status: 'error';
    messages: string[];
};
type ValidationResult = ValidationSuccess | ValidationFailure;

// Defined outside of the function to avoid re-creating the regex on each call
const letterThenAlphaNum = /^[a-zA-Z][a-zA-Z0-9_]*$/;
// TODO: Align this generic filename check with Illumina validation. The Illumina regex allows hyphens in sample prefixes,
// but this guard rejects them before format-specific validation runs.
const alphaNumThenAlphaNumAndDot = /^[a-zA-Z0-9][a-zA-Z0-9_.]*$/;
const reserved_r_keywords = new Set([
    'if',
    'else',
    'repeat',
    'while',
    'function',
    'for',
    'in',
    'next',
    'break',
    'TRUE',
    'FALSE',
    'NULL',
    'Inf',
    'NaN',
    'NA',
    'NA_integer_',
    'NA_real_',
    'NA_complex_',
    'NA_character_'
]);

/**
 * Checks whether a column name contains only letters, numbers, and underscores.
 * Checks whether the column name starts with a letter.
 * Checks whether the column name is not a reserved R keyword.
 */
const isValidColName = (name: string): boolean => {
    return letterThenAlphaNum.test(name) && !reserved_r_keywords.has(name);
};

/**
 * Checks whether a value is valid. A value is valid if it is an empty string
 * or a valid column name.
 */
export const isValidValue = (value: string): boolean => {
    return value === '' || isValidColName(value);
};

/**
 * Checks whether a filename is valid. A filename is valid if it contains
 * only letters, numbers, underscores, and dots. It should not start with
 * a dot. It should not be a reserved R keyword.
 */
const isValidFilename = (name: string): boolean => {
    return alphaNumThenAlphaNumAndDot.test(name) && !reserved_r_keywords.has(name);
};

/**
 * Validate given column names. Ensure they follow naming conventions and that
 * all required columns are present.
 */
const validateColNames = (columns: string[], requiredColumns: Set<string> | Array<string>): ValidationResult => {
    if (columns.length === 0) {
        // Don't think this is reachable with PapaParse result, but perform the
        // extra sanity check
        return {status: 'error', messages: ['CSV file has no columns']};
    }
    const errors: string[] = [];
    requiredColumns.forEach((col: string) => {
        if (!columns.includes(col)) {
            errors.push(`Missing required column: "${col}"`);
        }
    });
    columns.forEach((col: string) => {
        if (!isValidColName(col)) {
            errors.push(`Invalid column name: "${col}"`);
        }
    });
    if (errors.length > 0) {
        return {status: 'error', messages: errors};
    }
    return {status: 'success'};
};

/**
 * Validates a CSV file to ensure it meets the required format and data
 * integrity. This function checks for the presence of required columns,
 * illegal characters in column names, and illegal characters in the data
 * cells. It returns a boolean indicating whether the CSV is valid or not.
 *
 * This function performs the following checks:
 * 1. Checks if the required columns (SampleName and Filename) are present in the CSV file.
 * 2. Checks for illegal characters in the column names.
 * 3. Checks for illegal characters in the data cells of each column.
 * 4. Checks that Filename values follow Illumina or NCBI GEO naming conventions.
 * 5. Checks that Illumina filenames are compatible with the experiment's read type.
 *
 * @param csv The `contents` property represents the parsed rows. Each row is
 * an object with column names as keys.
 * @param sequencing_read_type The sequencing read type ('paired' or 'singled').
 * @returns ValidationSuccess if the CSV is valid, or ValidationFailure with error messages
 */
const validateRnaCSV = (csv: Record<string, string>[], sequencing_read_type: string): ValidationResult => {
    const reqColumns = new Set(['SampleName', 'Filename']);

    if (csv.length === 0) {
        return {status: 'error', messages: ['CSV file is empty']};
    }

    // Validate column names only for the first row
    const columns = Object.keys(csv[0]);
    const colNamesValidation = validateColNames(columns, reqColumns);
    if (colNamesValidation.status === 'error') {
        return colNamesValidation;
    }

    const seenFilenames = new Set<string>();

    // Validate data for all rows
    const errors: string[] = [];
    for (const row of csv) {
        // Check whether required columns have a value
        reqColumns.forEach((col: string) => {
            if (row[col] === '') {
                errors.push(`Missing value in required column "${col}"`);
            }
        });
        // Handle (required column) Filename explicitly, its format is different
        if (!isValidFilename(row.Filename)) {
            errors.push(`Invalid Filename value: "${row.Filename}"`);
        } else if (!isValidFilenameFormat(row.Filename, sequencing_read_type)) {
            // Filename passes basic character checks but doesn't match Illumina or NCBI GEO convention
            errors.push(
                `Filename "${row.Filename}" does not match the required Illumina ` +
                    `(e.g. SampleName_S1_L001_R1_001.fastq.gz) or NCBI GEO ` +
                    `(e.g. SRR123456_1.fastq.gz) naming convention`
            );
        } else if (isIlluminaFormat(row.Filename) && !isIlluminaReadTypeMatch(row.Filename, sequencing_read_type)) {
            // Illumina filename has R2 but experiment is single-end
            errors.push(
                `Filename "${row.Filename}" contains R2 but experiment is configured as ` +
                    `single-end (singled). Only R1 filenames are expected`
            );
        }
        // Check data in other columns
        for (const col of columns) {
            if (col !== 'Filename') {
                const cellValue = row[col];
                if (!isValidValue(cellValue)) {
                    errors.push(`Invalid value in column "${col}": ${cellValue}`);
                }
            }
        }
        // Ensure Filename is unique
        if (seenFilenames.has(row.Filename)) {
            errors.push(`Duplicate Filename value: "${row.Filename}"`);
        } else {
            seenFilenames.add(row.Filename);
        }
    }

    if (errors.length > 0) {
        return {status: 'error', messages: errors};
    }

    return {status: 'success'};
};

/**
 * Parses a CSV file and validates its contents.
 *
 * The CSV file must have a header row with required columns: SampleName and
 * Filename. These must have values in every row. Additional columns can be
 * added. Column names and values have certain restrictions placed on them,
 * mostly in light of ensuring they do not break the analysis pipeline.
 *
 * Filenames are validated against Illumina and NCBI GEO naming conventions.
 * Filenames that do not match will be rejected with a user-facing error
 * message rather than being silently ignored.
 *
 * @param file The CSV file to parse.
 * @param sequencing_read_type The sequencing read type ('paired' or 'singled'),
 * used to validate Illumina filename read numbers.
 * @returns array of objects representing the parsed rows where the keys are
 * the header values, rejects with a list of error messages
 */
export const parseAndValidateRnaCsv = async (file: File, sequencing_read_type: string): Promise<Record<string, string>[]> => {
    const data = await papaParse(file);
    const csvIsValid = validateRnaCSV(data, sequencing_read_type);
    if (csvIsValid.status === 'error') {
        throw csvIsValid.messages;
    } else {
        return data;
    }
};

const allNumbers = /^[0-9]+$/;

/**
 * Parses and validates metadata CSV for ChIP Seq and ATAC Seq.
 *
 * TODO: When creating analysis, we will want to know what sample is control
 * for what other sample. Do we want to make that link explicit in the database
 * or reconstruct it at that point?
 *
 * TODO: Might just want to add two wrapper functions for this that don't do
 * more than pass the right sequencingType. So a parseChipMetadataCsv and
 * parseAtacMetadataCsv.
 *
 * @returns array of objects each a sample definition, headers become keys. Rejects with a list of error messages.
 */
export const parseMetadataCsv = async (metadataF: File, sequencingType: 'ChIP Seq' | 'ATAC Seq'): Promise<Record<string, string>[]> => {
    const metadata = await papaParse(metadataF);

    if (metadata.length === 0) {
        throw ['Metadata CSV is empty'];
    }

    // Only a strict set of columns are allowed. I don't think we need to enforce an order though
    const isChipSeq = sequencingType === 'ChIP Seq';
    const allowedAndRequiredColumns = new Set(
        isChipSeq
            ? ['SampleName', 'Replicate', 'Factor', 'Tissue', 'Condition', 'Treatment', 'SampleType', 'ControlName']
            : ['SampleName', 'Replicate', 'Factor', 'Tissue', 'Condition', 'Treatment']
    );
    const actualColumns = new Set(Object.keys(metadata[0]));
    // @ts-ignore Our TS is too old for Set typing, but create-react-app prevents upgrade...
    const missingColumns = allowedAndRequiredColumns.difference(actualColumns);
    // @ts-ignore
    const superfluousColumns = actualColumns.difference(allowedAndRequiredColumns);

    if (missingColumns.size > 0) {
        throw [`Missing required columns: ${Array.from(missingColumns).join(', ')}`];
    }
    if (superfluousColumns.size > 0) {
        throw [`Extra columns: ${Array.from(superfluousColumns).join(', ')}`];
    }

    // Doing these validations a bit tangled as we loop through the rows once.
    // It's to avoid looping through the entire thing for each check.

    const allSamplenames = new Set<string>();
    const referencedControlNames = new Set<string>();

    // Need a value in every row for these columns.
    // Handling Replicate, Condition, Treatmeant, SampleType and ControlName separately, note that SampleType and ControlName only apply for ChIP seq
    const requiredColumns = ['SampleName', 'Factor', 'Tissue'];
    const isConditionEmpty = metadata[0].Condition === '';
    const isTreatmentEmpty = metadata[0].Treatment === '';
    const errors: string[] = [];

    for (const row of metadata) {
        requiredColumns.forEach((col: string) => {
            if (row[col] === '') {
                errors.push(`Missing value in required column "${col}"`);
            } else if (!isValidValue(row[col])) {
                errors.push(`Invalid ${col} value: "${row[col]}"`);
            }
        });

        // We use this to indicate IP samples without a control
        if (isChipSeq && row.SampleName === 'NoControl') {
            errors.push('Invalid SampleName value: "NoControl"');
        }

        allSamplenames.add(row.SampleName);

        // Replicate must be a string (because we don't parse) of digits
        if (!allNumbers.test(row.Replicate)) {
            errors.push(`Invalid Replicate value: "${row.Replicate}"`);
        }

        if (isChipSeq) {
            // TODO What about our RNA value restrictions?
            // ControlName depends on its SampleType
            // - If SampleType is Input, ControlName must be Input
            // - If SampleType is IP, ControlName must be another sample name or NoControl
            if (row.SampleType === 'Input') {
                if (row.ControlName !== 'Input') {
                    errors.push(`Invalid ControlName value: "${row.ControlName}"`);
                }
            } else if (row.SampleType === 'IP') {
                if (row.ControlName !== 'NoControl') {
                    if (row.ControlName === row.SampleName) {
                        errors.push(`ControlName references itself: "${row.ControlName}"`);
                    }
                    referencedControlNames.add(row.ControlName);
                }
            } else {
                // SampleType must be Input (Control) or IP (Experimental)
                errors.push(`Invalid SampleType value: "${row.SampleType}"`);
            }
        }

        if (!isValidValue(row.Condition)) {
            errors.push(`Invalid Condition value: "${row.Condition}"`);
        }
        if ((isConditionEmpty && row.Condition !== '') || (!isConditionEmpty && row.Condition === '')) {
            errors.push('Column "Condition" needs to have all values or be entirely empty');
        }
        if (!isValidValue(row.Treatment)) {
            errors.push(`Invalid Treatment value: "${row.Treatment}"`);
        }
        if ((isTreatmentEmpty && row.Treatment !== '') || (!isTreatmentEmpty && row.Treatment === '')) {
            errors.push('Column "Treatment" needs to have all values or be entirely empty');
        }
    }

    if (isChipSeq) {
        // @ts-ignore
        const nonExistentControlNames = referencedControlNames.difference(allSamplenames);
        if (nonExistentControlNames.size > 0) {
            errors.push(`ControlName references unknown sample: ${Array.from(nonExistentControlNames).join(', ')}`);
        }
    }
    // TODO: Q: Does a control require being referenced?

    if (errors.length > 0) {
        throw errors;
    }

    return metadata;
};

const allowedChipFilenameColumns = [
    ['SampleName', 'Filename'],
    ['SampleName', 'Filename_1', 'Filename_2'],
    ['SampleName', 'Filename_1', 'Filename_2', 'Filename_3'],
    ['SampleName', 'Filename_1', 'Filename_2', 'Filename_3', 'Filename_4'],
    ['SampleName', 'Filename_1_1', 'Filename_1_2'],
    ['SampleName', 'Filename_1_1', 'Filename_1_2', 'Filename_2_1', 'Filename_2_2'],
    ['SampleName', 'Filename_1_1', 'Filename_1_2', 'Filename_2_1', 'Filename_2_2', 'Filename_3_1', 'Filename_3_2'],
    [
        'SampleName',
        'Filename_1_1',
        'Filename_1_2',
        'Filename_2_1',
        'Filename_2_2',
        'Filename_3_1',
        'Filename_3_2',
        'Filename_4_1',
        'Filename_4_2'
    ]
];

const chipFilenameColumnsByReadType: Record<'singled' | 'paired', Record<number, string[]>> = {
    singled: {
        1: allowedChipFilenameColumns[0],
        2: allowedChipFilenameColumns[1],
        3: allowedChipFilenameColumns[2],
        4: allowedChipFilenameColumns[3]
    },
    paired: {
        2: allowedChipFilenameColumns[4],
        4: allowedChipFilenameColumns[5],
        6: allowedChipFilenameColumns[6],
        8: allowedChipFilenameColumns[7]
    }
};

/**
 * Checks whether columns in filenames CSV match exactly with an allowed pattern based on readtype and files per sample.
 *
 * Also ensure same order of columns since we turn things into an array to match RNA approach.
 */
const validChipFilenameColumns = (columns: string[], sequencing_read_type: 'paired' | 'singled', files_per_sample: number): boolean => {
    const expectedColumns = chipFilenameColumnsByReadType[sequencing_read_type][files_per_sample];
    if (!expectedColumns) {
        return false;
    }
    if (expectedColumns.length !== columns.length) {
        return false;
    }
    for (let i = 0; i < expectedColumns.length; i++) {
        if (expectedColumns[i] !== columns[i]) {
            return false;
        }
    }
    return true;
};

/**
 * Parses and validates the ChIP filenames CSV.
 *
 * Expects a CSV with SampleName and some combination of Filename* columns.
 * SampleName must be unique. Filenames must be unique and valid.
 *
 * @return mapping of sample names to filenames
 */
export const parseChipFilenamesCsv = async (
    filenamesF: File,
    sequencing_read_type: 'paired' | 'singled',
    files_per_sample: number
): Promise<Record<string, string[]>> => {
    const filenames = await papaParse(filenamesF);

    if (filenames.length === 0) {
        throw ['Filenames CSV is empty'];
    }

    const expectedColumns = chipFilenameColumnsByReadType[sequencing_read_type][files_per_sample];
    if (!expectedColumns) {
        throw [`Unsupported values in ${sequencing_read_type}: ${files_per_sample}`];
    }

    const columns = Object.keys(filenames[0]);
    if (!validChipFilenameColumns(columns, sequencing_read_type, files_per_sample)) {
        throw [
            `Invalid or missing column names in filenames CSV for ${sequencing_read_type} read type with ${files_per_sample} files per sample. Expected: ${expectedColumns.join(', ')}`
        ];
    }

    const errors: string[] = [];

    const seenFilenames = new Set<string>();

    const result: Record<string, string[]> = {};
    for (const row of filenames) {
        const sampleName = row.SampleName;

        if (result[sampleName] !== undefined) {
            errors.push(`Duplicate sample name in filenames CSV: ${sampleName}`);
            continue;
        }

        result[sampleName] = [];
        // Add all Filename* columns
        for (const col of columns) {
            if (col === 'SampleName') continue;
            const filename = row[col];
            if (filename === '') {
                errors.push(`Empty value in column "${col}" for sample "${sampleName}"`);
                continue;
            }
            if (!isValidFilename(filename)) {
                errors.push(`Invalid filename: "${filename}"`);
                continue;
            }
            if (!isValidFilenameFormat(filename, sequencing_read_type)) {
                errors.push(`Invalid filename format for ${sequencing_read_type}: "${filename}"`);
                continue;
            }
            if (seenFilenames.has(filename)) {
                errors.push(`Duplicate filename: "${filename}"`);
                continue;
            }
            seenFilenames.add(filename);
            result[sampleName].push(filename);
        }
    }

    if (errors.length > 0) {
        throw errors;
    }

    return result;
};

/**
 * Validates the ChIP replicates in the metadata CSV.
 *
 * Checks that for each combination of Factor, Tissue, Condition, Treatment,
 * SampleType, there are replicates 1, 2, 3, ..., up to the maximum replicate
 * number present for that combination. So valid combos are: 1; 1,2; 1,2,3;
 * etc. The max number is only checked within a combination, not across all
 * combinations.
 *
 * Also checks for duplicate replicates.
 *
 * In the future, probably want to let the user review the combinations we
 * deduced. See TROV-1659.
 *
 * @returns ValidationResult with status 'success' or 'error' and messages
 */
export const validateChipReplicates = (metadata: Record<string, string>[]): ValidationResult => {
    // Transform metadata into format expected by groupAndSortSamplesByReplicates
    const samples = metadata.map(row => ({
        name: row.SampleName,
        other_categories: {
            Factor: row.Factor,
            Tissue: row.Tissue,
            Condition: row.Condition,
            Treatment: row.Treatment,
            // sampleType does not exist for atac analysis,
            // we set it to "ATAC" so we can use the logic here
            SampleType: row?.SampleType || 'ATAC',
            Replicate: row.Replicate
        }
    }));

    try {
        // Use the utility function to validate grouping and replicates
        groupAndSortSamplesByReplicates(samples);
        return {status: 'success'};
    } catch (error) {
        // Convert error messages to ValidationResult format
        const errorMessage = error instanceof Error ? error.message : String(error);
        const messages = errorMessage.split('\n').filter(msg => msg.trim() !== '');
        return {status: 'error', messages};
    }
};

/**
 * Validates the filenames CSV against the metadata CSV.
 *
 * Checks that all sample names in the filenames CSV are present in the
 * metadata CSV and vice versa.
 */
export const validateChipCsv = (metadata: Record<string, string>[], filenames: Record<string, string[]>): ValidationResult => {
    const errors: string[] = [];
    const metaSamplenames = new Set<string>(metadata.map(row => row.SampleName));
    const fileSamplenames = new Set<string>(Object.keys(filenames));

    // Check if all sample names in filenames CSV are present in metadata CSV
    for (const sampleName of Array.from(fileSamplenames)) {
        if (!metaSamplenames.has(sampleName)) {
            errors.push(`SampleName "${sampleName}" in filenames CSV not found in metadata CSV`);
        }
    }

    // Check if all sample names in metadata CSV are present in filenames CSV
    for (const sampleName of Array.from(metaSamplenames)) {
        if (!fileSamplenames.has(sampleName)) {
            errors.push(`SampleName "${sampleName}" in metadata CSV not found in filenames CSV`);
        }
    }

    if (errors.length > 0) {
        return {status: 'error', messages: errors};
    }

    return {status: 'success'};
};
